// frontend/src/pages/manufacturing/StockAllocationManagement.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  Divider,
  InputAdornment,
  CircularProgress,
  LinearProgress,
  TablePagination,
  FormControlLabel,
  Checkbox,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Inventory,
  Warning,
  Search,
  Refresh,
  RemoveCircleOutline,
  PriorityHigh,
  Add,
  Clear,
  CheckCircle,
  ErrorOutline,
  FilterList
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { manufacturingAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../../hooks/usePageTitle';

// Debounce hook for search
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const StockAllocationManagement = () => {
  const navigate = useNavigate();
  usePageTitle('Stock Allocations');

  // Data state
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stockLevels, setStockLevels] = useState({});
  const [allocations, setAllocations] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);

  // Server-side pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // Filters (sent to server)
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [urgencyFilter, setUrgencyFilter] = useState('all');

  // Client-side filters (applied after fetching)
  const [allocationFilter, setAllocationFilter] = useState('all');
  const [hasStockFilter, setHasStockFilter] = useState(false);

  // Debounced search
  const debouncedSearch = useDebounce(search, 500);

  // Dialog state
  const [allocateDialog, setAllocateDialog] = useState({ open: false, line: null, order: null });
  const [unallocateDialog, setUnallocateDialog] = useState({ open: false, line: null, order: null, allocation: null });
  const [allocateQty, setAllocateQty] = useState('');
  const [unallocateQty, setUnallocateQty] = useState('');
  const [dialogLoading, setDialogLoading] = useState(false);

  // Stats (separate API call)
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalLines: 0,
    linesNeedingAllocation: 0,
    ordersFullyAllocated: 0,
    overdueOrders: 0,
    canAllocateNow: 0
  });

  // Load orders with server-side filtering
  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      // Build query params for server-side filtering
      const params = {
        page: page + 1, // API uses 1-based pagination
        page_size: rowsPerPage,
        ordering: 'due_date'
      };

      // Search filter
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      // Status filter
      if (statusFilter === 'active') {
        params.hide_complete = 'true';
      } else if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      // Urgency filter
      if (urgencyFilter === 'overdue') {
        params.overdue = 'true';
      }

      const response = await manufacturingAPI.customerOrders.list(params);
      const ordersData = response.data.results || [];
      setOrders(ordersData);
      setTotalCount(response.data.count || 0);

      // Fetch stock levels and allocations for displayed orders
      await loadStockAndAllocations(ordersData);

      setDataLoaded(true);
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error('Failed to load order data');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, statusFilter, urgencyFilter]);

  // Separate function to load stock and allocations for current page's orders
  const loadStockAndAllocations = async (ordersData) => {
    const uniqueItemIds = new Set();
    const lineIds = [];

    for (const order of ordersData) {
      for (const line of (order.lines || [])) {
        uniqueItemIds.add(line.product);
        lineIds.push(line.id);
      }
    }

    // Fetch stock levels in parallel
    const stockMap = {};
    const itemIdArray = Array.from(uniqueItemIds);

    await Promise.all(
      itemIdArray.map(async (itemId) => {
        try {
          const stockResponse = await manufacturingAPI.stockLevels.list({ item: itemId });
          const stockData = stockResponse.data.results || stockResponse.data;
          if (stockData.length > 0) {
            stockMap[itemId] = stockData[0];
          }
        } catch (err) {
          console.error(`Failed to fetch stock for item ${itemId}:`, err);
        }
      })
    );
    setStockLevels(prev => ({ ...prev, ...stockMap }));

    // Fetch allocations in parallel
    const allocMap = {};
    await Promise.all(
      lineIds.map(async (lineId) => {
        try {
          const allocResponse = await manufacturingAPI.stockAllocations.list({ order_line: lineId });
          const allocData = allocResponse.data.results || allocResponse.data;
          allocMap[lineId] = allocData;
        } catch (err) {
          console.error(`Failed to fetch allocations for line ${lineId}:`, err);
        }
      })
    );
    setAllocations(prev => ({ ...prev, ...allocMap }));
  };

  // Load stats separately
  const loadStats = useCallback(async () => {
    try {
      const response = await manufacturingAPI.customerOrders.dashboardStats();
      const data = response.data;
      setStats({
        totalOrders: data.total_active || 0,
        ordersFullyAllocated: data.ready_to_ship || 0,
        overdueOrders: data.overdue || 0,
        linesNeedingAllocation: 0, // Would need custom endpoint
        canAllocateNow: 0, // Would need custom endpoint
        totalLines: 0
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  // Load data when filters/pagination change
  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Load stats on mount
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter, urgencyFilter, allocationFilter, hasStockFilter]);

  // Helper functions
  const getAvailableStock = useCallback((itemId) => {
    const stock = stockLevels[itemId];
    return stock ? parseFloat(stock.quantity_available || 0) : 0;
  }, [stockLevels]);

  const getLineAllocations = useCallback((lineId) => {
    return allocations[lineId] || [];
  }, [allocations]);

  const getShortage = (line) => {
    const ordered = parseFloat(line.quantity_ordered || 0);
    const allocated = parseFloat(line.quantity_allocated || 0);
    const shipped = parseFloat(line.quantity_shipped || 0);
    return Math.max(0, ordered - allocated - shipped);
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const now = new Date();
    const due = new Date(dueDate);
    return Math.ceil((due - now) / (1000 * 60 * 60 * 24));
  };

  const isLineFullyAllocated = (line) => {
    const ordered = parseFloat(line.quantity_ordered || 0);
    const allocated = parseFloat(line.quantity_allocated || 0);
    const shipped = parseFloat(line.quantity_shipped || 0);
    return (allocated + shipped) >= ordered;
  };

  const isOrderFullyAllocated = useCallback((order) => {
    return (order.lines || []).every(line => isLineFullyAllocated(line));
  }, []);

  const lineCanBeAllocated = useCallback((line) => {
    const shortage = getShortage(line);
    const available = getAvailableStock(line.product);
    return shortage > 0 && available > 0;
  }, [getAvailableStock]);

  // Apply client-side filters (allocation status, has stock)
  const filteredOrders = useMemo(() => {
    let result = [...orders];

    // Allocation filter (client-side since it depends on line data)
    if (allocationFilter === 'needs_allocation') {
      result = result.filter(order => !isOrderFullyAllocated(order));
    } else if (allocationFilter === 'fully_allocated') {
      result = result.filter(order => isOrderFullyAllocated(order));
    } else if (allocationFilter === 'partial') {
      result = result.filter(order => {
        const lines = order.lines || [];
        if (lines.length === 0) return false;
        const allocatedLines = lines.filter(l => isLineFullyAllocated(l)).length;
        return allocatedLines > 0 && allocatedLines < lines.length;
      });
    }

    // Has stock available filter (client-side)
    if (hasStockFilter) {
      result = result.filter(order =>
        (order.lines || []).some(line => lineCanBeAllocated(line))
      );
    }

    return result;
  }, [orders, allocationFilter, hasStockFilter, isOrderFullyAllocated, lineCanBeAllocated]);

  // Get lines to display for an order based on filters
  const getDisplayLines = useCallback((order) => {
    let lines = order.lines || [];

    if (allocationFilter === 'needs_allocation') {
      lines = lines.filter(line => !isLineFullyAllocated(line));
    }

    if (hasStockFilter) {
      lines = lines.filter(line => lineCanBeAllocated(line));
    }

    return lines;
  }, [allocationFilter, hasStockFilter, lineCanBeAllocated]);

  // Helpers for display
  const getUrgencyChip = (dueDate) => {
    if (!dueDate) return null;
    const daysUntil = getDaysUntilDue(dueDate);

    if (daysUntil < 0) {
      return <Chip size="small" color="error" icon={<PriorityHigh />} label={`${Math.abs(daysUntil)}d overdue`} />;
    } else if (daysUntil === 0) {
      return <Chip size="small" color="error" label="Due today" />;
    } else if (daysUntil <= 3) {
      return <Chip size="small" color="warning" label={`${daysUntil}d`} />;
    } else if (daysUntil <= 7) {
      return <Chip size="small" color="info" label={`${daysUntil}d`} />;
    }
    return <Chip size="small" variant="outlined" label={`${daysUntil}d`} />;
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'default',
      'confirmed': 'info',
      'in_production': 'warning',
      'ready_to_ship': 'success',
      'partially_shipped': 'secondary',
      'complete': 'success',
      'cancelled': 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'new': 'New',
      'confirmed': 'Confirmed',
      'in_production': 'In Production',
      'ready_to_ship': 'Ready to Ship',
      'partially_shipped': 'Partially Shipped',
      'complete': 'Complete',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Actions
  const handleAllocateClick = (order, line) => {
    const available = getAvailableStock(line.product);
    const shortage = getShortage(line);
    const maxAlloc = Math.min(available, shortage);

    setAllocateDialog({
      open: true,
      order: order,
      line: line,
      available: available,
      shortage: shortage,
      maxAlloc: maxAlloc
    });
    setAllocateQty(maxAlloc > 0 ? maxAlloc.toFixed(0) : '');
  };

  const handleUnallocateClick = (order, line, allocation) => {
    setUnallocateDialog({
      open: true,
      order: order,
      line: line,
      allocation: allocation
    });
    setUnallocateQty(parseFloat(allocation.quantity_allocated).toFixed(0));
  };

  const executeAllocate = async () => {
    const qty = parseFloat(allocateQty);
    if (qty <= 0) {
      toast.error('Invalid quantity');
      return;
    }

    setDialogLoading(true);
    try {
      await manufacturingAPI.stockAllocations.allocateFromStock({
        order_id: allocateDialog.order.id,
        item_id: allocateDialog.line.product,
        quantity: qty
      });
      toast.success(`Allocated ${qty} units to ${allocateDialog.order.order_number}`);
      setAllocateDialog({ open: false, line: null, order: null });
      setAllocateQty('');
      loadOrders();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to allocate');
    } finally {
      setDialogLoading(false);
    }
  };

  const executeUnallocate = async () => {
    const qty = parseFloat(unallocateQty);
    if (qty <= 0) {
      toast.error('Invalid quantity');
      return;
    }

    setDialogLoading(true);
    try {
      await manufacturingAPI.stockAllocations.unallocate({
        allocation_id: unallocateDialog.allocation.id,
        quantity: qty
      });
      toast.success(`Returned ${qty} units to stock from ${unallocateDialog.order.order_number}`);
      setUnallocateDialog({ open: false, line: null, order: null, allocation: null });
      setUnallocateQty('');
      loadOrders();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to unallocate');
    } finally {
      setDialogLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('active');
    setAllocationFilter('all');
    setUrgencyFilter('all');
    setHasStockFilter(false);
    setPage(0);
  };

  const handleRefresh = () => {
    loadOrders();
    loadStats();
  };

  const hasActiveFilters = search || statusFilter !== 'active' || allocationFilter !== 'all' || urgencyFilter !== 'all' || hasStockFilter;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Inventory color="primary" />
            Stock Allocation Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            View and manage stock allocations for customer orders
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={20} /> : <Refresh />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Active Orders</Typography>
              <Typography variant="h5">{stats.totalOrders}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Ready to Ship</Typography>
              <Typography variant="h5" color="success.main">{stats.ordersFullyAllocated}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Overdue</Typography>
              <Typography variant="h5" color="error.main">{stats.overdueOrders}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterList color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>Filters</Typography>
          {hasActiveFilters && (
            <Button
              size="small"
              startIcon={<Clear />}
              onClick={clearFilters}
              sx={{ ml: 'auto' }}
            >
              Clear All
            </Button>
          )}
        </Box>

        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search orders, customers, items..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          {/* Order Status */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Order Status</InputLabel>
              <Select
                value={statusFilter}
                label="Order Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="active">Active Orders</MenuItem>
                <MenuItem value="all">All Orders</MenuItem>
                <Divider />
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="in_production">In Production</MenuItem>
                <MenuItem value="ready_to_ship">Ready to Ship</MenuItem>
                <MenuItem value="partially_shipped">Partially Shipped</MenuItem>
                <MenuItem value="complete">Complete</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Allocation Status */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Allocation Status</InputLabel>
              <Select
                value={allocationFilter}
                label="Allocation Status"
                onChange={(e) => setAllocationFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="needs_allocation">Needs Allocation</MenuItem>
                <MenuItem value="partial">Partially Allocated</MenuItem>
                <MenuItem value="fully_allocated">Fully Allocated</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Urgency */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Urgency</InputLabel>
              <Select
                value={urgencyFilter}
                label="Urgency"
                onChange={(e) => setUrgencyFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Has Stock Available */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasStockFilter}
                  onChange={(e) => setHasStockFilter(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircle fontSize="small" color="success" />
                  <Typography variant="body2">Only lines with stock available</Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>

        {/* Active filter chips */}
        {hasActiveFilters && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {search && (
              <Chip
                size="small"
                label={`Search: "${search}"`}
                onDelete={() => setSearch('')}
              />
            )}
            {statusFilter !== 'active' && (
              <Chip
                size="small"
                label={`Status: ${statusFilter === 'all' ? 'All' : getStatusLabel(statusFilter)}`}
                onDelete={() => setStatusFilter('active')}
              />
            )}
            {allocationFilter !== 'all' && (
              <Chip
                size="small"
                label={`Allocation: ${allocationFilter.replace(/_/g, ' ')}`}
                onDelete={() => setAllocationFilter('all')}
                color="warning"
              />
            )}
            {urgencyFilter !== 'all' && (
              <Chip
                size="small"
                label={`Urgency: ${urgencyFilter.replace(/_/g, ' ')}`}
                onDelete={() => setUrgencyFilter('all')}
                color="error"
              />
            )}
            {hasStockFilter && (
              <Chip
                size="small"
                label="Has stock available"
                onDelete={() => setHasStockFilter(false)}
                color="success"
              />
            )}
          </Box>
        )}

        {/* Results count */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing <strong>{filteredOrders.length}</strong> of <strong>{totalCount}</strong> orders
            {debouncedSearch && ` matching "${debouncedSearch}"`}
          </Typography>
          {loading && <CircularProgress size={20} />}
        </Box>
      </Paper>

      {loading && !dataLoaded && <LinearProgress sx={{ mb: 2 }} />}

      {/* Alerts */}
      {dataLoaded && stats.overdueOrders > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorOutline />}>
          <strong>{stats.overdueOrders} order{stats.overdueOrders > 1 ? 's are' : ' is'} overdue!</strong> These need immediate attention.
        </Alert>
      )}

      {/* Orders Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell width={140}>Order</TableCell>
              <TableCell width={180}>Customer</TableCell>
              <TableCell width={110}>Due</TableCell>
              <TableCell>Item</TableCell>
              <TableCell align="right" width={80}>Ordered</TableCell>
              <TableCell align="right" width={80}>Allocated</TableCell>
              <TableCell align="right" width={80}>Shipped</TableCell>
              <TableCell align="right" width={80}>Shortage</TableCell>
              <TableCell align="right" width={90}>Available</TableCell>
              <TableCell width={100}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !dataLoaded ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Loading orders and stock data...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary" gutterBottom>No orders found matching the filters</Typography>
                  {hasActiveFilters && (
                    <Button
                      size="small"
                      startIcon={<Clear />}
                      onClick={clearFilters}
                      sx={{ mt: 1 }}
                    >
                      Clear All Filters
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              filteredOrders.map((order) => {
                const displayLines = getDisplayLines(order);

                if (displayLines.length === 0) return null;

                const allLines = order.lines || [];
                const lineCount = allLines.length;
                const orderFullyAllocated = isOrderFullyAllocated(order);

                return (
                  <React.Fragment key={order.id}>
                    {displayLines.map((line, lineIndex) => {
                      const ordered = parseFloat(line.quantity_ordered || 0);
                      const allocated = parseFloat(line.quantity_allocated || 0);
                      const shipped = parseFloat(line.quantity_shipped || 0);
                      const shortage = getShortage(line);
                      const available = getAvailableStock(line.product);
                      const lineAllocations = getLineAllocations(line.id);
                      const isFirstLine = lineIndex === 0;
                      const canAllocate = shortage > 0 && available > 0;

                      return (
                        <TableRow
                          key={`${order.id}-${line.id}`}
                          sx={{
                            bgcolor: isFirstLine ? 'grey.50' : 'white',
                            borderTop: isFirstLine ? '2px solid' : 'none',
                            borderTopColor: 'divider',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          {/* Order info - only on first line */}
                          {isFirstLine ? (
                            <>
                              <TableCell
                                rowSpan={displayLines.length}
                                sx={{
                                  verticalAlign: 'top',
                                  borderRight: '1px solid',
                                  borderRightColor: 'divider',
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: 'action.hover' }
                                }}
                                onClick={() => navigate(`/manufacturing/customer-orders/${order.id}`)}
                              >
                                <Typography variant="body2" fontWeight={600} color="primary">
                                  {order.order_number}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={getStatusLabel(order.status)}
                                  color={getStatusColor(order.status)}
                                  sx={{ mt: 0.5 }}
                                />
                                {orderFullyAllocated && (
                                  <Tooltip title="Fully allocated">
                                    <CheckCircle fontSize="small" color="success" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                                  </Tooltip>
                                )}
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {lineCount} line{lineCount > 1 ? 's' : ''}
                                </Typography>
                              </TableCell>
                              <TableCell
                                rowSpan={displayLines.length}
                                sx={{
                                  verticalAlign: 'top',
                                  borderRight: '1px solid',
                                  borderRightColor: 'divider'
                                }}
                              >
                                <Typography variant="body2" fontWeight={500}>
                                  {order.customer_name}
                                </Typography>
                                {order.customer_reference && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Ref: {order.customer_reference}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell
                                rowSpan={displayLines.length}
                                sx={{
                                  verticalAlign: 'top',
                                  borderRight: '1px solid',
                                  borderRightColor: 'divider'
                                }}
                              >
                                <Typography variant="body2">{formatDate(order.due_date)}</Typography>
                                <Box sx={{ mt: 0.5 }}>
                                  {getUrgencyChip(order.due_date)}
                                </Box>
                              </TableCell>
                            </>
                          ) : null}

                          {/* Line item details */}
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>{line.product_display}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {line.product_name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{ordered.toFixed(0)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              color={allocated >= ordered ? 'success.main' : allocated > 0 ? 'warning.main' : 'text.secondary'}
                            >
                              {allocated.toFixed(0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color={shipped > 0 ? 'success.main' : 'text.secondary'}>
                              {shipped.toFixed(0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {shortage > 0 ? (
                              <Typography variant="body2" fontWeight={600} color="error.main">
                                {shortage.toFixed(0)}
                              </Typography>
                            ) : (
                              <Tooltip title="Fully allocated">
                                <CheckCircle fontSize="small" color="success" />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight={canAllocate ? 600 : 400}
                              color={available > 0 ? (canAllocate ? 'success.main' : 'text.primary') : 'text.secondary'}
                            >
                              {available.toFixed(0)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {canAllocate && (
                                <Tooltip title={`Allocate up to ${Math.min(available, shortage).toFixed(0)} units`}>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleAllocateClick(order, line)}
                                  >
                                    <Add />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {lineAllocations.length > 0 && (
                                <Tooltip title="Remove allocation">
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={() => handleUnallocateClick(order, line, lineAllocations[0])}
                                  >
                                    <RemoveCircleOutline />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {!canAllocate && lineAllocations.length === 0 && shortage > 0 && (
                                <Tooltip title="No stock available">
                                  <ErrorOutline fontSize="small" color="disabled" />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Allocate Dialog */}
      <Dialog
        open={allocateDialog.open}
        onClose={() => { setAllocateDialog({ open: false, line: null, order: null }); setAllocateQty(''); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Inventory color="success" /> Allocate Stock
        </DialogTitle>
        <DialogContent>
          {allocateDialog.line && allocateDialog.order && (
            <>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2">Order: {allocateDialog.order.order_number}</Typography>
                <Typography variant="body2" color="text.secondary">{allocateDialog.order.customer_name}</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2">Item: <strong>{allocateDialog.line.product_display}</strong></Typography>
                <Typography variant="body2" color="text.secondary">{allocateDialog.line.product_name}</Typography>
                <Divider sx={{ my: 1 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Shortage</Typography>
                    <Typography variant="h6" color="error.main">{allocateDialog.shortage?.toFixed(0)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Available Stock</Typography>
                    <Typography variant="h6" color="success.main">{allocateDialog.available?.toFixed(0)}</Typography>
                  </Grid>
                </Grid>
              </Box>
              <TextField
                label="Quantity to Allocate"
                type="number"
                fullWidth
                value={allocateQty}
                onChange={(e) => setAllocateQty(e.target.value)}
                inputProps={{
                  min: 1,
                  max: allocateDialog.maxAlloc,
                  step: 1
                }}
                helperText={`Max: ${allocateDialog.maxAlloc?.toFixed(0) || 0}`}
                autoFocus
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAllocateDialog({ open: false, line: null, order: null }); setAllocateQty(''); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={executeAllocate}
            disabled={!allocateQty || parseFloat(allocateQty) <= 0 || dialogLoading}
          >
            {dialogLoading ? <CircularProgress size={20} /> : 'Allocate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unallocate Dialog */}
      <Dialog
        open={unallocateDialog.open}
        onClose={() => { setUnallocateDialog({ open: false, line: null, order: null, allocation: null }); setUnallocateQty(''); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" /> Remove Allocation
        </DialogTitle>
        <DialogContent>
          {unallocateDialog.line && unallocateDialog.allocation && unallocateDialog.order && (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Return allocated stock to available inventory:
              </DialogContentText>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2">Order: {unallocateDialog.order.order_number}</Typography>
                <Typography variant="body2" color="text.secondary">{unallocateDialog.order.customer_name}</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2">Item: <strong>{unallocateDialog.line.product_display}</strong></Typography>
                <Typography variant="body2">
                  Currently Allocated: <strong>{parseFloat(unallocateDialog.allocation.quantity_allocated).toFixed(0)}</strong> units
                </Typography>
              </Box>
              <TextField
                label="Quantity to Remove"
                type="number"
                fullWidth
                value={unallocateQty}
                onChange={(e) => setUnallocateQty(e.target.value)}
                inputProps={{
                  min: 1,
                  max: unallocateDialog.allocation.quantity_allocated,
                  step: 1
                }}
                helperText={`Max: ${parseFloat(unallocateDialog.allocation.quantity_allocated).toFixed(0)}`}
                autoFocus
              />
              {unallocateDialog.order.status === 'ready_to_ship' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This order is "Ready to Ship". Removing allocation may change its status.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUnallocateDialog({ open: false, line: null, order: null, allocation: null }); setUnallocateQty(''); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={executeUnallocate}
            disabled={!unallocateQty || parseFloat(unallocateQty) <= 0 || dialogLoading}
          >
            {dialogLoading ? <CircularProgress size={20} /> : 'Remove Allocation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockAllocationManagement;